/* eslint-disable @typescript-eslint/no-shadow */
import { useChannelContext } from "client/components/channel"
import { useContentReportMutation } from "client/graphql"
import { TextAreaInput } from "client/packages/commerce/components/ui/inputs"
import {
  RadioButton,
  RadioLabel,
  RadioOption,
} from "client/packages/commerce/components/ui/styles/FormElementStyles"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from "react-intl"
import LoaderButton from "shared/components/buttons/LoaderButton"
import { useComponentId } from "shared/hooks/useComponentId"
import styled from "styled-components"

const messages = defineMessages({
  hateful: {
    id: "reportCategory.hateful",
    defaultMessage: "Hateful",
  },
  sexuallyExplicit: {
    id: "reportCategory.sexuallyExplicit",
    defaultMessage: "Sexually Explicit",
  },
  violence: {
    id: "reportCategory.violence",
    defaultMessage: "Violence",
  },
  harassmentOrBullying: {
    id: "reportCategory.harassmentOrBullying",
    defaultMessage: "Harassment or Bullying",
  },
  spamOrMisleading: {
    id: "reportCategory.spamOrMisleading",
    defaultMessage: "Spam or Misleading",
  },
  other: {
    id: "reportCategory.other",
    defaultMessage: "Other",
  },
  genericError: {
    id: "reportingError",
    defaultMessage:
      "Oops, something went wrong. The report could not be sent.",
  },
})

const FormWrapper = styled.div`
  max-width: 100%;
  width: 350px;
`

const FormItemWrapper = styled.div`
  display: grid;
  grid-gap: 15px;
  grid-template-columns: 1fr;
`

const CategoryWrapper = styled.div`
  margin-top: 20px;
`
const TextAreaLabel = styled.label`
  display: inline-block;
  margin-bottom: 8px;

  font-size: ${(props) => props.theme.input.textSize};
  color: ${(props) => props.theme.text.dark};
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0;
`

const ReportButton = styled(LoaderButton)`
  width: min-content;
  align-self: flex-end;
`

const ErrorMessage = styled.p`
  width: 100%;
  font-size: ${(props) => props.theme.input.errorText};
  color: ${(props) => props.theme.colors.error};
  margin: 0;
`

enum ReportCategory {
  Hateful = "hateful",
  SexuallyExplicit = "sexuallyExplicit",
  Violence = "violence",
  HarassmentOrBullying = "harassmentOrBullying",
  SpamOrMisleading = "spamOrMisleading",
  Other = "other",
}

interface Props {
  onSuccess: any
}

const ReportForm = ({
  onSuccess,
  intl,
}: Props & WrappedComponentProps) => {
  const componentId = useComponentId()
  const formId = `reportForm-${componentId}`
  const { channelId } = useChannelContext()
  const formHook = useForm({
    defaultValues: { description: "" },
    reValidateMode: "onSubmit",
  })
  const [category, setCategory] = useState<ReportCategory | null>(null)
  const [reportChannelMutation, { data, loading, error }] =
    useContentReportMutation({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const onSubmit = async (value) => {
    reportChannelMutation({
      variables: {
        input: {
          id: channelId,
          category,
          description: value.description,
        },
      },
    })
  }

  useEffect(() => {
    if (data) {
      onSuccess()
    }
  }, [data])

  useEffect(() => {
    if (error) {
      setErrorMessage(intl.formatMessage(messages.genericError))
    }
  }, [error])

  const onCategoryChange = (event) => {
    setErrorMessage(null)
    setCategory(event.target.value)
  }

  return (
    <FormWrapper>
      <form onSubmit={formHook.handleSubmit(onSubmit)} id={formId}>
        <FormItemWrapper>
          <CategoryWrapper>
            {Object.values(ReportCategory).map((category) => (
              <RadioOption key={category}>
                <RadioButton
                  id={category}
                  type="radio"
                  value={category}
                  name="reportCategory"
                  onChange={onCategoryChange}
                />
                <RadioLabel htmlFor={category}>
                  {intl.formatMessage(messages[category])}
                </RadioLabel>
              </RadioOption>
            ))}
          </CategoryWrapper>
          <div>
            <TextAreaLabel htmlFor="reportDescription">
              <FormattedMessage
                id="reportDescriptionLabel"
                defaultMessage="Description"
              />
            </TextAreaLabel>
            <TextAreaInput
              id="reportDescription"
              name="description"
              error={formHook.formState.errors.description}
              formHook={formHook}
              autoComplete="off"
              showLabel={false}
              onChange={() => {
                setErrorMessage(null)
              }}
            />
          </div>
          <ButtonWrapper>
            <ReportButton
              type="submit"
              disabled={category === null}
              isLoading={loading}
              form={formId}
            >
              <FormattedMessage
                id="reportForm.submitReport"
                defaultMessage="Submit"
              />
            </ReportButton>
          </ButtonWrapper>
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </FormItemWrapper>
      </form>
    </FormWrapper>
  )
}

export default injectIntl(ReportForm)
